/* =============================================================
   PawShift — ps-reviews.js
   • Animate star-breakdown bars on scroll (IntersectionObserver)
   • Filter pills — show/hide review cards, reinsert rebuttal at pos 4
   • "All Reviews" hides extra-only cards (data-extra="true")
   • Category filter shows matching cards including extra-only ones
   • Show only the first 5 matching reviews per pill; a minimal
     three-dot control reveals the rest (auto-collapses on pill switch)
   • Quiz arrival: round-robin the dog's patterns to the top, never 3 alike in a row
   ============================================================= */

(function () {
  'use strict';

  /* ─── Bar animation ─── */
  const bars = document.getElementById('ps-reviews-bars');
  if (bars) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            bars.classList.add('is-visible');
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(bars);
  }

  /* ─── Filter pills + progressive reveal ─── */
  const grid = document.getElementById('ps-reviews-grid');
  if (!grid) return;

  const CAP = 5;  // reviews shown per pill before "show more"
  const STEP = 2; // each "show more" click reveals this many more
  const rebuttal = grid.querySelector('.ps-review-card[data-tag="rebuttal"]');
  const filterBtns = document.querySelectorAll('.ps-reviews__filter-btn');
  const moreWrap = document.getElementById('ps-reviews-more');
  const moreBtn = moreWrap ? moreWrap.querySelector('.ps-reviews__more') : null;

  let currentTag = 'all';
  let shown = CAP; // how many matching reviews are currently revealed

  // Her quiz patterns, priority order (top-2) — from ps-quiz-handoff.js.
  // Empty when there's no quiz context -> authored order, unchanged.
  let patterns = (window.psQuiz && window.psQuiz.patterns) || [];

  // Snapshot each review's AUTHORED index once, now, before any float reorders the DOM.
  // The float tie-breaks on this — NOT the live DOM index — so repeated renders / pill
  // switches can't corrupt the baseline order.
  [...grid.querySelectorAll('.ps-review-card:not([data-tag="rebuttal"])')]
    .forEach((card, i) => { card.dataset.psOrder = i; });

  function reinsertRebuttal() {
    if (!rebuttal) return;
    const visibleCards = [...grid.querySelectorAll('.ps-review-card:not([data-tag="rebuttal"])')].filter(
      (c) => c.style.display !== 'none'
    );
    // Always inject at position 4 (after index 3)
    if (visibleCards.length >= 4) {
      grid.insertBefore(rebuttal, visibleCards[3]);
    } else if (visibleCards.length > 0) {
      grid.appendChild(rebuttal);
    }
    rebuttal.style.display = '';
  }

  // Order the current view's matches: round-robin across her patterns, and never let
  // 3 of the same pattern sit in a row (break the run with one non-matching review).
  // Deterministic: same quiz answers -> same order. Ties break on authored index (psOrder).
  function patternOrder(cards) {
    if (!patterns.length) return cards;

    // authored order first so buckets are stable regardless of prior DOM reordering
    var ordered = cards.slice().sort(function (a, b) {
      return (parseInt(a.dataset.psOrder, 10) || 0) - (parseInt(b.dataset.psOrder, 10) || 0);
    });

    function hits(card, p) { return (card.dataset.pattern || '').split(',').indexOf(p) !== -1; }

    // bucket each matched card under the FIRST of her patterns it hits (priority order)
    var used = [];
    var buckets = patterns.map(function (p) {
      return ordered.filter(function (c) {
        if (used.indexOf(c) !== -1) return false;
        if (hits(c, p)) { used.push(c); return true; }
        return false;
      });
    });
    var variety = ordered.filter(function (c) { return used.indexOf(c) === -1; });

    // round-robin across buckets -> [{card, pat}]
    var matched = [], any = true;
    while (any) {
      any = false;
      for (var b = 0; b < buckets.length; b++) {
        if (buckets[b].length) { matched.push({ card: buckets[b].shift(), pat: patterns[b] }); any = true; }
      }
    }

    // weave: break any run of >2 same-pattern with one variety card
    var out = [], mi = 0, vi = 0, run = 0, lastPat = null;
    while (mi < matched.length) {
      var m = matched[mi];
      var nextRun = (m.pat === lastPat) ? run + 1 : 1;
      if (nextRun > 2 && vi < variety.length) { out.push(variety[vi++]); run = 0; lastPat = null; continue; }
      out.push(m.card); run = nextRun; lastPat = m.pat; mi++;
    }
    while (vi < variety.length) out.push(variety[vi++]);
    return out;
  }

  function render() {
    const reviewCards = [...grid.querySelectorAll('.ps-review-card:not([data-tag="rebuttal"])')];

    // 1. Cards matching the active pill (DOM order)
    let matches = reviewCards.filter((card) =>
      currentTag === 'all' ? card.dataset.extra !== 'true' : card.dataset.tag === currentTag
    );

    // 1b. Quiz pattern float — round-robin across her patterns, never 3 alike in a row
    //     (see patternOrder). Reorders the DOM so CSS shows the float.
    if (patterns.length) {
      matches = patternOrder(matches);
      matches.forEach(function (card) { grid.appendChild(card); }); // reflect order in the DOM
    }

    // 2. Hide everything, then reveal up to the current count
    reviewCards.forEach((card) => { card.style.display = 'none'; });
    const limit = Math.min(shown, matches.length);
    for (let i = 0; i < limit; i++) matches[i].style.display = '';

    // 3. Rebuttal pinned at position 4 among the visible cards
    reinsertRebuttal();

    // 4. Reveal control — visible only while more remain to show
    if (moreWrap) {
      moreWrap.style.display = matches.length > limit ? '' : 'none';
    }
  }

  function setFilter(tag) {
    currentTag = tag;
    shown = CAP; // reset to the first 5 whenever the pill changes
    render();
  }

  filterBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      filterBtns.forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      setFilter(btn.dataset.filter);
    });
  });

  if (moreBtn) {
    moreBtn.addEventListener('click', () => {
      shown += STEP; // reveal 2 more each click
      render();
    });
  }

  // If the patterns are published after this script runs, re-render with the float.
  document.addEventListener('ps:pattern', function (e) {
    var arr = (e.detail && e.detail.patterns) || [];
    if (arr.length && arr.join() !== patterns.join()) { patterns = arr; render(); }
  });

  // Initial state — All Reviews, collapsed to first 5
  setFilter('all');
})();
